import React, { useContext, useEffect, useRef } from "react";
import { UserPreferencesContext } from "../../../utilities/contexts/UserPreferences";
import useSelectorId from "../../../utilities/hooks/useSelectorId";
import { FAContext } from "../../../utilities/contexts/FontAwesomeContext";

/**
 * ECLThemeImage allows us to render a image based on the theme stored in the User's Preferences
 *
 * @export
 * @param {object} props
 *      - alt - string - alt labeling for image, will be passed through translate function
 *      - className - string - class name for the html element
 *      - id - string - id for the image element
 *      - svgAtrributes - object - add any attributes, including styling {height: "200px", width: "200px"}
 *      - fontAwesome - string - used to look up the FontAwesome src path, should have no fill specified, will change color according to theme
 *      - src - path to the SVG src to be used if it's not FontAwesome, should have no fill specified, will change color according to theme
 *      - style - react style object - pass in a style object into the div container
 * 
 *      IF YOU DON'T WANT AUTOMATIC COLOR RESPONSIVITY
 *      - lightThemeRoute - string - route of image in light theme mode with fill specified
 *      - darkThemeRoute - string - route of the image in dark theme mode with fill specified
 *      OR
 *      - colorOverride - will take the SVG and fill it with this color in all themes

 *      
 * @return {ReactComponent} 
 */
export default function ECLThemeImage(props) {
   const instanceId = useSelectorId();
   const userPrefs = useContext(UserPreferencesContext);
   const updateFontAwesome = useContext(FAContext);
   //Portals do funky things, so actually really need this to be a ref
   const imageContainer = useRef(null);
   //User Preferences may not be available, in which case we should consult local storage
   let theme;
   if (userPrefs) {
      theme = userPrefs.theme;
   } else {
      theme = localStorage.getItem("userTheme");
   }

   useEffect(() => {
      if (props.fontAwesome) {
         updateFontAwesome(props.fontAwesome);
      }
   }, [props.fontAwesome]);

   // Early Escape for font awesome icons
   if (props.fontAwesome) {
      let color;
      if (props.colorOverride) {
         color = props.colorOverride;
      } else {
         color = theme === "Dark" ? "white" : "black";
      }
      let style = { color: color };
      if (props.style) {
         style = { ...style, ...props.style };
      }

      return (
         <svg className="themed-svg-container" style={style}>
            <use
               href={`#fa-symbol-${props.fontAwesome}`}
               title={props.alt}
               height="100%"
               width="100%"
            ></use>
         </svg>
      );
   }

   let src;
   if (props.lightThemeRoute && props.darkThemeRoute) {
      src = theme === "Dark" ? props.darkThemeRoute : props.lightThemeRoute;
   } else {
      src = props.src;
   }

   return (
      <img
         id={
            props.id
               ? props.id + "-svg-container"
               : `svg-container-${instanceId}`
         }
         className="themed-svg-container"
         src={src}
         ref={imageContainer}
         style={props.style ? props.style : {}}
      ></img>
   );
}
